import React from "react";
import {
  IconButton,
  Tooltip,
  useTheme,
} from "@mui/material";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";

import {
  useEdgeMentorEnabled,
} from "./EdgeMentorToggle";
import {
  getActiveEdgeMentorModule,
  subscribeToActiveEdgeMentorModule,
} from "./edgeMentorModuleRegistry";

const useActiveEdgeMentorModule = () => {
  const [activeModule, setActiveModule] =
    React.useState(() =>
      getActiveEdgeMentorModule(),
    );

  React.useEffect(
    () =>
      subscribeToActiveEdgeMentorModule(
        setActiveModule,
      ),
    [],
  );

  return activeModule;
};

const ActiveModuleToggle = ({ activeModule }) => {
  const theme = useTheme();

  const {
    enabled,
    toggleEnabled,
  } = useEdgeMentorEnabled(
    activeModule.moduleKey,
    true,
  );

  // Match AvatarComponent's foreground treatment while enabled.
  const enabledIconColor =
    theme.palette.mode === "dark"
      ? "#ffffff"
      : theme.palette.text.primary;

  const tooltip = enabled
    ? "Disable Edge Mentor"
    : "Enable Edge Mentor";

  return (
    <Tooltip title={tooltip}>
      <IconButton
        size="small"
        onClick={toggleEnabled}
        aria-label={tooltip}
        aria-pressed={enabled}
        data-edge-mentor-toggle="true"
        data-edge-mentor-module={
          activeModule.moduleKey
        }
        sx={{
          color: enabled
            ? enabledIconColor
            : "inherit",
          backgroundColor: enabled
            ? "primary.main"
            : "transparent",
          border: (muiTheme) =>
            `1px solid ${
              enabled
                ? muiTheme.palette.primary.main
                : muiTheme.palette.divider
            }`,
          borderRadius: 1,
          width: 34,
          height: 34,
          transition: (muiTheme) =>
            muiTheme.transitions.create(
              [
                "background-color",
                "border-color",
                "color",
              ],
              {
                duration:
                  muiTheme.transitions.duration.shortest,
              },
            ),

          "&:hover": {
            backgroundColor: (muiTheme) => {
              if (enabled) {
                return muiTheme.palette.primary.main;
              }

              return muiTheme.palette.mode === "dark"
                ? "rgba(34, 211, 238, 0.12)"
                : "rgba(64, 67, 172, 0.08)";
            },
          },
        }}
      >
        <SchoolOutlinedIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );
};

/**
 * Single universal top-navbar Edge Mentor button.
 *
 * It only appears while an Edge Mentor-enabled module is active. The active
 * module registers itself, so the navbar does not depend on fragile pathname
 * guessing. Future Piping / Structural / Tank hosts can register their own
 * module keys and automatically reuse this same physical icon.
 */
const EdgeMentorNavbarToggle = () => {
  const activeModule =
    useActiveEdgeMentorModule();

  if (!activeModule?.moduleKey) {
    return null;
  }

  return (
    <ActiveModuleToggle
      activeModule={activeModule}
    />
  );
};

export default EdgeMentorNavbarToggle;
